/**
 * Team-mirrored projection (02-arena §7) and its inverse for the floor-point
 * aim model (05-input §3). The camera is a pure function of team plus an
 * optional team-local pose (spec §2); the zero pose is the legacy fixed camera.
 */
import { ARENA_LENGTH, CAM_BACK, CAM_HEIGHT, CAM_HFOV_DEG, CAM_NEAR, CAM_PITCH_DEG, DISC_FLIGHT_Y, VIRTUAL_H, VIRTUAL_W } from '../../sim/constants.js'
import type { Arena, Team, Vec2 } from '../../sim/types.js'

export interface Projected { sx: number; sy: number; scale: number; depth: number; visible: boolean }
/** Team-local camera pose (spec §2). yaw 0 looks +z (local); positive yaw turns toward +x (screen right). */
export interface CameraPose { x: number; z: number; yaw: number }
export interface CameraParams { back: number; height: number; pitchDeg: number; hfovDeg: number; near: number; pose: CameraPose }

export const ZERO_POSE: Readonly<CameraPose> = Object.freeze({ x: 0, z: 0, yaw: 0 })
export const DEFAULT_CAMERA: CameraParams = { back: CAM_BACK, height: CAM_HEIGHT, pitchDeg: CAM_PITCH_DEG, hfovDeg: CAM_HFOV_DEG, near: CAM_NEAR, pose: ZERO_POSE }
export const withPose = (cam: CameraParams, pose: CameraPose): CameraParams => ({ ...cam, pose })
/** S10 (spec §3): the arena's own camera — `arena.cam` over the corridor_a defaults, zero pose. corridor_xl's back/height
 *  are 4/3 of corridor_a's at the same pitch and FOV, so the room frames like today and athletes read 3/4 size. */
export const cameraFor = (arena: Arena): CameraParams => ({ ...DEFAULT_CAMERA, ...(arena.cam ?? {}), pose: ZERO_POSE })
export const isZeroPose = (p: CameraPose): boolean => p.x === 0 && p.z === 0 && p.yaw === 0
export const focalLength = (cam: CameraParams = DEFAULT_CAMERA): number => (VIRTUAL_W / 2) / Math.tan((cam.hfovDeg * Math.PI) / 360)

/** The one place mirroring exists (02 §1). Its own inverse. */
export function toTeamLocal(x: number, z: number, team: Team, arenaLength: number = ARENA_LENGTH): { lx: number; lz: number } {
  return team === 0 ? { lx: x, lz: z } : { lx: -x, lz: arenaLength - z }
}

/** Team-local → camera-aligned (yaw removed, pose-relative). */
function toCameraFrame(lx: number, lz: number, pose: CameraPose): { cx: number; cz: number } {
  const rx = lx - pose.x, rz = lz - pose.z
  const c = Math.cos(pose.yaw), s = Math.sin(pose.yaw)
  return { cx: rx * c - rz * s, cz: rx * s + rz * c }
}
/** Camera-aligned → team-local. Inverse of toCameraFrame. */
function fromCameraFrame(cx: number, cz: number, pose: CameraPose): { lx: number; lz: number } {
  const c = Math.cos(pose.yaw), s = Math.sin(pose.yaw)
  return { lx: pose.x + cx * c + cz * s, lz: pose.z - cx * s + cz * c }
}

/** A point in camera space after pose, translation and pitch: `dx` right, `yc` up, `zc` depth along the view axis. */
export interface CamSpace { dx: number; yc: number; zc: number }

export function toCameraSpace(x: number, y: number, z: number, team: Team, arena: Arena, cam: CameraParams = DEFAULT_CAMERA): CamSpace {
  const { lx, lz } = toTeamLocal(x, z, team, arena.length)
  const { cx, cz } = toCameraFrame(lx, lz, cam.pose)
  const pitch = (cam.pitchDeg * Math.PI) / 180
  const dx = cx, dy = y - cam.height, dz = cz + cam.back
  const cp = Math.cos(pitch), sp = Math.sin(pitch)
  return { dx, yc: dy * cp + dz * sp, zc: -dy * sp + dz * cp }
}

export function projectCamSpace(p: CamSpace, cam: CameraParams = DEFAULT_CAMERA): Projected {
  const visible = p.zc >= cam.near
  const safeZ = Math.max(cam.near, p.zc)
  const f = focalLength(cam)
  return { sx: VIRTUAL_W / 2 + (f * p.dx) / safeZ, sy: VIRTUAL_H / 2 - (f * p.yc) / safeZ, scale: f / safeZ, depth: p.zc, visible }
}

export function project(x: number, y: number, z: number, team: Team, arena: Arena, cam: CameraParams = DEFAULT_CAMERA): Projected {
  return projectCamSpace(toCameraSpace(x, y, z, team, arena, cam), cam)
}

const lerpCam = (a: CamSpace, b: CamSpace, t: number): CamSpace => ({ dx: a.dx + (b.dx - a.dx) * t, yc: a.yc + (b.yc - a.yc) * t, zc: a.zc + (b.zc - a.zc) * t })

/** Clip a segment to the half-space zc >= near. Null when wholly behind the camera. Points behind the
 *  near plane project through `Math.max(near, zc)` otherwise, which folds them onto the screen as garbage. */
export function clipSegmentNear(a: CamSpace, b: CamSpace, near: number): [CamSpace, CamSpace] | null {
  const ina = a.zc >= near, inb = b.zc >= near
  if (ina && inb) return [a, b]
  if (!ina && !inb) return null
  const t = (near - a.zc) / (b.zc - a.zc)
  const cut = lerpCam(a, b, t); cut.zc = near
  return ina ? [a, cut] : [cut, b]
}

/** Sutherland–Hodgman against the single plane zc = near. Empty when wholly behind the camera. */
export function clipPolygonNear(pts: readonly CamSpace[], near: number): CamSpace[] {
  const out: CamSpace[] = []
  for (let i = 0; i < pts.length; i++) {
    const cur = pts[i], prev = pts[(i + pts.length - 1) % pts.length]
    const inc = cur.zc >= near, inp = prev.zc >= near
    if (inc !== inp) { const t = (near - prev.zc) / (cur.zc - prev.zc); const cut = lerpCam(prev, cur, t); cut.zc = near; out.push(cut) }
    if (inc) out.push(cur)
  }
  return out
}

/** World xz of the floor point `dist` metres ahead of the camera pose along its yaw. */
export function aheadOnFloor(cam: CameraParams, dist: number, team: Team, arena: Arena): Vec2 {
  const l = fromCameraFrame(0, dist, cam.pose)
  const w = toTeamLocal(l.lx, l.lz, team, arena.length)
  return { x: w.lx, z: w.lz }
}

/** Ray from the camera through virtual pixel (sx, sy), in the camera-aligned frame (pose not yet applied). */
function cameraRay(sx: number, sy: number, cam: CameraParams): { ox: number; oy: number; oz: number; dx: number; dy: number; dz: number } {
  const f = focalLength(cam)
  const pitch = (cam.pitchDeg * Math.PI) / 180
  const cp = Math.cos(pitch), sp = Math.sin(pitch)
  const xc = (sx - VIRTUAL_W / 2) / f
  const yc = (VIRTUAL_H / 2 - sy) / f
  const zc = 1
  return { ox: 0, oy: cam.height, oz: -cam.back, dx: xc, dy: yc * cp - zc * sp, dz: yc * sp + zc * cp }
}

/** Intersect the cursor ray with plane y = planeY. Returns WORLD xz, or null if
 *  the hit is not in front of the camera. */
export function unprojectToPlane(sx: number, sy: number, team: Team, arena: Arena, planeY: number, cam: CameraParams = DEFAULT_CAMERA): Vec2 | null {
  const r = cameraRay(sx, sy, cam)
  if (Math.abs(r.dy) < 1e-9) return null
  const t = (planeY - r.oy) / r.dy
  if (t <= 0) return null
  const cx = r.ox + r.dx * t, cz = r.oz + r.dz * t
  if (cz + cam.back < cam.near) return null
  const l = fromCameraFrame(cx, cz, cam.pose)
  const w = toTeamLocal(l.lx, l.lz, team, arena.length)
  return { x: w.lx, z: w.lz }
}

/** Horizon fallback. Zero pose: the far back-wall plane (team-local z = L), as before.
 *  Posed camera: 40 m ahead along the ray's horizontal bearing. World xz. */
export function unprojectToFarWall(sx: number, sy: number, team: Team, arena: Arena, cam: CameraParams = DEFAULT_CAMERA): Vec2 {
  const r = cameraRay(sx, sy, cam)
  if (isZeroPose(cam.pose)) {
    const dz = r.dz > 1e-6 ? r.dz : 1e-6
    const t = (arena.length - r.oz) / dz
    const hw = arena.width / 2
    const lx = Math.max(-hw, Math.min(hw, r.ox + r.dx * t))
    const w = toTeamLocal(lx, arena.length, team, arena.length)
    return { x: w.lx, z: w.lz }
  }
  const h = Math.hypot(r.dx, r.dz) || 1
  const l = fromCameraFrame(r.ox + (r.dx / h) * 40, r.oz + (r.dz / h) * 40, cam.pose)
  const w = toTeamLocal(l.lx, l.lz, team, arena.length)
  return { x: w.lx, z: w.lz }
}

export interface FloorAim { angle: number; hit: Vec2; kept: boolean }

/** 05 §3 steps 1-3: cursor -> floor point at DISC_FLIGHT_Y (far-wall clamp
 *  above the horizon) -> bearing from the predicted player position. Within
 *  0.3 m of the athlete the previous aim is kept. */
export function floorAim(cursor: { x: number; y: number }, playerPos: { x: number; z: number }, team: Team, arena: Arena, prevAngle: number, cam: CameraParams = DEFAULT_CAMERA): FloorAim {
  let hit = unprojectToPlane(cursor.x, cursor.y, team, arena, DISC_FLIGHT_Y, cam)
  if (!hit) hit = unprojectToFarWall(cursor.x, cursor.y, team, arena, cam)
  const hw = arena.width / 2
  hit = { x: Math.max(-hw, Math.min(hw, hit.x)), z: Math.max(0, Math.min(arena.length, hit.z)) }
  const dx = hit.x - playerPos.x, dz = hit.z - playerPos.z
  if (Math.hypot(dx, dz) < 0.3 || !Number.isFinite(dx) || !Number.isFinite(dz)) return { angle: prevAngle, hit, kept: true }
  return { angle: Math.atan2(dz, dx), hit, kept: false }
}
